import { useState } from 'react';

import {
  UniqueIdentifier,
  DragEndEvent,
  DragStartEvent,
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  Modifier,
  Active,
  useDndMonitor,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import { ColumnOrderState, flexRender } from '@tanstack/react-table';
import styled from 'styled-components';

import { PortalDragOverlay } from '@components/drag-and-drop';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { getIsTableAction } from './use-column-sizing-vars';

const MODIFIERS: Array<Modifier> = [restrictToHorizontalAxis];

export const useReorderDnd = ({
  columnOrder,
  setColumnOrder,
}: {
  columnOrder: ColumnOrderState;
  setColumnOrder(order: ColumnOrderState): void;
}) => {
  const [draggingId, setDraggingId] = useState<UniqueIdentifier>();
  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setDraggingId(undefined);

    if (getIsTableAction(active.id as string)) {
      return;
    }

    if (active && over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);
      // arrayMove reads a -1 as "the last one", so a column the order has never
      // heard of would drag whatever sits at the end of the table instead of
      // itself. The store keeps the order complete; this is the backstop.
      if (oldIndex === -1 || newIndex === -1) {
        return;
      }
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
    }
  };

  const onDragStart = (event: DragStartEvent) => {
    setDraggingId(event.active.id);
  };

  const onDragCancel = () => {
    setDraggingId(undefined);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
    useSensor(KeyboardSensor, {})
  );

  return {
    draggingId,
    onDragCancel,
    onDragEnd,
    onDragStart,
    sensors,
    modifiers: MODIFIERS,
    collisionDetection: closestCenter,
  };
};

export const DndOverlay = () => {
  const [active, setActive] = useState<Active>();

  useDndMonitor({
    onDragCancel: () => {
      setActive(undefined);
    },
    onDragEnd: () => {
      setActive(undefined);
    },
    onDragStart: (event) => setActive(event.active),
  });

  const header = active?.data.current?.header;

  return (
    <PortalDragOverlay>
      {header && (
        <OverlayContainer>
          <HeaderContent>
            <Text color="textSecondary" font="semibold14">
              {flexRender(header.column.columnDef.header, header.getContext())}
            </Text>
          </HeaderContent>
        </OverlayContainer>
      )}
    </PortalDragOverlay>
  );
};

export const HeaderContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  paddingRight: pxToRem(4),
  height: pxToRem(24),
});

export const OverlayContainer = styled.div({
  backgroundColor: theme.color.backgroundLightDark,
  height: pxToRem(48),
  transform: `translate(-${pxToRem(-8)}, ${pxToRem(4)})`,
  boxShadow: theme.shadow.high,
  display: 'flex',
  alignItems: 'center',
  borderRadius: pxToRem(4),
  padding: pxToRem(8),
  cursor: 'grab !important',
});

export const DragBorder = styled.div({
  zIndex: 9999,
  position: 'absolute',
  bottom: 0,
  top: 0,
  right: 0,
  width: 2,
  backgroundColor: theme.color.blue100,
});
